import {
  CheckCircleOutlined,
  EditOutlined,
  MinusCircleOutlined,
} from '@ant-design/icons'
import {
  ACTheme,
  Button,
  ColumnProps,
  Table,
  TableProps,
  UserAvatar,
  getUserDisplayNames,
  useACTheme,
} from 'admin-web-ui'
import moment from 'moment'
import React, { useMemo } from 'react'
import { Link } from 'react-router-dom'
import styled from 'styled-components'
import { Project, User } from '../../api'
import { CustomerGroup } from '../../auth'

const TableWrapper: React.FC<TableProps<User>> = (props) => (
  <Table<User> {...props} />
)

const StyledTable = styled(TableWrapper)`
  width: 100%;

  thead tr th {
    background-color: ${({ theme }) => theme.greys.glamdring};
    font-size: 12px;
  }
`

export const LastSignIn = (props: {
  date?: string
  className?: string
}): JSX.Element => (
  <span className={props.className}>
    {props.date ? moment(props.date).format('MMM DD, YYYY z') : '-'}
  </span>
)

const StyledLastSignIn = styled(LastSignIn)`
  max-width: 100px;
  white-space: nowrap;
`

const ProjectName = styled.div`
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  max-width: 100px;
`

const UserActionButtonContainer = styled.div`
  display: flex;
  justify-content: flex-end;
  white-space: nowrap;
  @media screen and (max-width: 884px) {
    display: flex;
    flex-direction: column;
  }

  button {
    padding-left: 8px;
    padding-right: 8px;
  }
`

export const UserActionButton = styled(Button)<{ fontcolor: string }>`
  border: none;
  color: ${(props) => props.fontcolor};
  font-size: 20px;
  background-color: transparent;

  &:hover,
  &[disabled],
  &[disabled]:hover {
    background-color: transparent;
  }
`

// Sort mutates the projects in place, hence the array declaration and spreading
// in the projects to create a new reference.
export const Projects = (props: { projects: Project[] }): JSX.Element => (
  <div>
    {[...props.projects]
      .sort((projectA, projectB) => {
        return projectA.name.localeCompare(projectB.name)
      })
      .map((project) => (
        <ProjectName key={project.id}>{project.name}</ProjectName>
      ))}
  </div>
)

interface Props {
  currentUsername: string
  users: User[]
  onDisable: (user: User) => void
  loading?: boolean
  pendingUsernameToDisable?: string
  disableUserLoading?: boolean
}

export const makeTableColumns = (
  props: Props,
  theme: ACTheme,
): ColumnProps<User>[] => [
  {
    title: 'Name',
    dataIndex: 'name',
    key: 'name',
    defaultSortOrder: 'ascend',
    render(_, user) {
      return (
        <UserAvatar
          type="default"
          userInfo={user}
          userDisabledText="(Disabled)"
        />
      )
    },
    sorter(userA, userB): number {
      const nameA = getUserDisplayNames(userA)[0] || ''
      const nameB = getUserDisplayNames(userB)[0] || ''

      return nameA.localeCompare(nameB)
    },
  },
  {
    title: 'Access Admin',
    dataIndex: 'groups',
    key: 'access-admin',
    align: 'center',
    width: '130px',
    render(_, user) {
      return (
        user.groups.some((group) => group === CustomerGroup.AccessAdmin) && (
          <CheckCircleOutlined />
        )
      )
    },
    sorter(userA, userB): number {
      const isAccessAdminA = userA.groups.some(
        (group) => group === CustomerGroup.AccessAdmin,
      )

      const isAccessAdminB = userB.groups.some(
        (group) => group === CustomerGroup.AccessAdmin,
      )

      return +isAccessAdminA - +isAccessAdminB
    },
  },
  {
    className: 'automation-emailAddress',
    title: 'Email',
    dataIndex: 'emailAddress',
    key: 'emailAddress',
    ellipsis: true,
  },
  {
    title: 'Last Sign In',
    dataIndex: 'lastSignIn',
    key: 'lastSignIn',
    width: '150px',
    render(_, user) {
      return <StyledLastSignIn date={user.lastSignIn} />
    },
  },
  {
    title: 'Projects',
    dataIndex: 'projects',
    key: 'projects',
    width: '150px',
    render(_, user) {
      return <Projects projects={user.projects} />
    },
  },
  {
    title: '',
    key: 'actions',
    width: '76px',
    render(_, user) {
      const isPendingDelete =
        user.username === props.pendingUsernameToDisable &&
        props.disableUserLoading
      const isSelf = props.currentUsername === user.username

      return (
        <UserActionButtonContainer>
          <UserActionButton
            title="Edit"
            id="edit-user-button"
            fontcolor={theme.colors.sudoBlue}
            disabled={
              (user.username === props.pendingUsernameToDisable &&
                props.disableUserLoading) ||
              !user.enabled
            }
          >
            <Link
              to={`./${user.username}`}
              className={`automation-EditUserButton_${user.emailAddress
                .split('@')[0]
                .replace('+', '')}`}
            >
              <EditOutlined />
            </Link>
          </UserActionButton>
          <UserActionButton
            title="Disable"
            id="disable-user-button"
            fontcolor={theme.colors.coral}
            disabled={isPendingDelete || isSelf || !user.enabled}
            onClick={() => props.onDisable(user)}
          >
            <MinusCircleOutlined />
          </UserActionButton>
        </UserActionButtonContainer>
      )
    },
  },
]

export const ManageAccountUsersList = (props: Props): React.ReactElement => {
  const theme = useACTheme()
  const columns = useMemo(() => makeTableColumns(props, theme), [props, theme])

  return (
    <StyledTable
      tableLayout="fixed"
      rowKey="username"
      pagination={false}
      dataSource={props.users}
      columns={columns}
      loading={props.loading}
    />
  )
}

ManageAccountUsersList.defaultProps = {
  loading: false,
}
